import { Box, WizardStepRenderParams, useOnNext } from '@factorialco/deprecated-design-system'
import {
  BankingEntitiesBankAccountNumberFormatEnum,
  CurrencySelectorCurrencyFragment,
  ValidateBankingBankAccountNumberDocument,
} from 'generated/resources/graphql'

import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import {
  accountNumberExtraFieldLabel,
  accountNumberLabel,
  bankAccountFormatOptions,
} from 'modules/banking/lib/bankAccountNumbersHelpers'
import CurrencySelector from 'modules/finance/components/CurrencySelector'

import { WizardManualAccountData } from '../../types'
import { WizardStepLayout } from '../WizardStepLayout'

interface Props {
  currencies: CurrencySelectorCurrencyFragment[]
  errors: {
    accountNumber?: string
    complementaryData?: string
  }
  onErrorsChange: (errors: { accountNumber?: string; complementaryData?: string }) => void
}

export const BankAccountManualInformationWizardStep = ({
  data: wizardData,
  onDataChanged,
  currencies,
  errors,
  onErrorsChange,
}: WizardStepRenderParams<WizardManualAccountData> & Props) => {
  const [validateBankingInformation] = useMutation(ValidateBankingBankAccountNumberDocument, {
    extractorFn: (data) => data.bankingMutations.validateBankingBankAccountNumber,
  })
  const bankAccountManualInfoForm = useForm({
    defaultValues: {
      accountNumber: wizardData.accountNumber,
      complementaryData: wizardData.complementaryData,
      accountNumberType: wizardData.accountNumberType,
    },
    onSubmit: async () => {
      const variables = {
        accountNumber: wizardData.accountNumber || '',
        complementaryData: wizardData.complementaryData,
        format: wizardData.accountNumberType,
      }

      const result = await validateBankingInformation({ variables })

      const handled = handleResult(result, (err) => {
        bankAccountManualInfoForm.setErrors({
          accountNumber: err['accountNumber'],
          complementaryData: err['complementaryData'],
        })
      })

      if (!handled.success) {
        onErrorsChange({
          accountNumber: i18n.t('employee_form.bank_info.invalid_format'),
          complementaryData: !!bankAccountManualInfoForm.values.complementaryData
            ? i18n.t('employee_form.bank_info.invalid_format')
            : undefined,
        })
      }
      return handled
    },
  })

  const submitForm = async () => {
    const submitResult = await bankAccountManualInfoForm.submitForm()

    if (!submitResult.success) {
      throw new Error(submitResult.errors.join())
    }
  }

  const accountLabel = accountNumberLabel(wizardData.accountNumberType)
  const extraFieldLabel = accountNumberExtraFieldLabel(wizardData.accountNumberType)

  useOnNext(submitForm)

  const handleFieldChanged = useUnsafeCallback(
    (fieldName: string, fieldValue: string | undefined | number) => {
      onDataChanged({
        ...wizardData,
        [fieldName]: fieldValue,
      })
    },
    [wizardData, onDataChanged]
  )

  const onAccountNumberChange = useUnsafeCallback(
    (value: string) => {
      handleFieldChanged('accountNumber', value)
      onErrorsChange({})
    },
    [handleFieldChanged, onErrorsChange]
  )

  const onExtraFieldChange = useUnsafeCallback(
    (value: string) => {
      handleFieldChanged('complementaryData', value)
      onErrorsChange({})
    },
    [handleFieldChanged, onErrorsChange]
  )

  const onCurrencyChange = useUnsafeCallback(
    (value: string) => {
      onDataChanged({ ...wizardData, currency: value })
    },
    [wizardData, onDataChanged]
  )

  const onAccountAliasChange = useUnsafeCallback(
    (value: string) => {
      onDataChanged({ ...wizardData, accountAlias: value })
    },
    [wizardData, onDataChanged]
  )

  const onAccountNumberTypeChange = useUnsafeCallback(
    (newFormat: string) => {
      if (newFormat == 'other' || newFormat == 'clabe') {
        onDataChanged({
          ...wizardData,
          complementaryData: '',
          accountNumberType: newFormat,
        })
      } else {
        onDataChanged({
          ...wizardData,
          // eslint-disable-next-line no-type-assertion/no-type-assertion
          accountNumberType: newFormat as BankingEntitiesBankAccountNumberFormatEnum,
        })
      }
      onErrorsChange({})
    },
    [onDataChanged, onErrorsChange, wizardData]
  )

  return (
    <WizardStepLayout
      title={i18n.t('banking.accounts.new_wizard.step_3_0.title')}
      subtitle={i18n.t('banking.accounts.new_wizard.step_3_0.subtitle')}
    >
      <Box style={{ width: '500px' }} flexNoShrink flexDirection="column" gap="s8">
        <TextField
          value={wizardData.accountAlias}
          label={i18n.t('banking.accounts.side_panel.account_alias')}
          placeholder={i18n.t('banking.accounts.side_panel.account_alias')}
          onChange={onAccountAliasChange}
        ></TextField>

        <Select
          label={i18n.t('employee_form.bank_info.format')}
          value={wizardData.accountNumberType}
          options={bankAccountFormatOptions()}
          onChange={onAccountNumberTypeChange}
        />

        <TextField
          mandatory
          value={wizardData.accountNumber}
          label={accountLabel}
          placeholder={i18n.t('banking.accounts.side_panel.account_number')}
          onChange={onAccountNumberChange}
          error={errors.accountNumber}
        ></TextField>

        {extraFieldLabel && (
          <TextField
            label={extraFieldLabel}
            value={wizardData.complementaryData}
            onChange={onExtraFieldChange}
            error={errors.accountNumber}
          />
        )}
        <CurrencySelector
          mandatory
          currencies={currencies}
          value={wizardData.currency}
          onChange={onCurrencyChange}
        />
      </Box>
    </WizardStepLayout>
  )
}
